import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Copy } from 'lucide-react';
import { Button, Dialog } from '../ui';
import { useConsoleI18n } from './bind';

// Copying to the clipboard, and secrets that are shown exactly once.

/** Copies `text`; false where neither the Clipboard API nor the legacy fallback works (http origins, old browsers). */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall back below */
  }
  try {
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const ok = typeof document.execCommand === 'function' && document.execCommand('copy');
    area.remove();
    return ok;
  } catch {
    return false;
  }
}

/** `copy(text)` plus `copied`, true for `ms` after a successful copy (the button says "Copied" meanwhile). */
export function useCopy(ms = 1500) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);
  const copy = useCallback(
    async (text: string) => {
      const ok = await copyText(text);
      if (ok) {
        setCopied(true);
        clearTimeout(timer.current);
        timer.current = setTimeout(() => setCopied(false), ms);
      }
      return ok;
    },
    [ms]
  );
  return { copy, copied };
}

/** A small button that copies `text` and says so for a moment. */
export function CopyButton({
  text,
  label,
  icon = false,
  variant = 'ghost',
  onCopied,
}: {
  text: string;
  label?: ReactNode;
  /** A copy icon before the label. */
  icon?: boolean;
  variant?: 'ghost' | 'secondary';
  onCopied?: () => void;
}) {
  const { t } = useConsoleI18n();
  const { copy, copied } = useCopy();
  return (
    <Button
      size="sm"
      variant={variant}
      icon={icon ? <Copy size={14} /> : undefined}
      onClick={() => void copy(text).then((ok) => ok && onCopied?.())}
    >
      {copied ? t('copy.copied') : (label ?? t('copy.copy'))}
    </Button>
  );
}

/** A secret (key, token, password) in monospace with its copy button. */
export function SecretReveal({
  value,
  label,
  className,
  'data-testid': testId,
}: {
  value: string;
  label?: ReactNode;
  className?: string;
  'data-testid'?: string;
}) {
  return (
    <div className={['nyk-key-reveal', className].filter(Boolean).join(' ')} data-testid={testId}>
      {label && <span className="nyk-ui-eyebrow">{label}</span>}
      <code>{value}</code>
      <CopyButton text={value} />
    </div>
  );
}

/**
 * A secret the server returns once (it keeps only a hash): shown in a dialog that a backdrop press cannot close, with
 * copy and an explicit "I have stored it".
 */
export function SecretDialog({
  title,
  value,
  children,
  ackLabel,
  onClose,
  maxWidth = 640,
  'data-testid': testId,
}: {
  title: ReactNode;
  value: string;
  /** More about where the secret goes (after the "shown once" sentence). */
  children?: ReactNode;
  ackLabel?: ReactNode;
  onClose: () => void;
  maxWidth?: number;
  'data-testid'?: string;
}) {
  const { t } = useConsoleI18n();
  return (
    <Dialog
      open
      onClose={onClose}
      title={title}
      maxWidth={maxWidth}
      closeOnBackdrop={false}
      footer={
        <Button variant="secondary" onClick={onClose}>
          {ackLabel ?? t('copy.stored')}
        </Button>
      }
    >
      <p className="nyk-card-intro">
        {t('copy.once')} {children}
      </p>
      <SecretReveal value={value} data-testid={testId} />
    </Dialog>
  );
}
